import { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Navigation, Phone, Utensils, Clock, Star } from 'lucide-react';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom restaurant marker icon
const createRestaurantIcon = (color = '#F97316') => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 36px; height: 36px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/>
        <path d="M7 2v20"/>
        <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>
      </svg>
    </div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36]
  });
};

// User location marker
const userLocationIcon = L.divIcon({
  className: 'user-marker',
  html: `<div style="background-color: #3B82F6; width: 20px; height: 20px; border-radius: 50%; border: 4px solid white; box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5), 0 2px 4px rgba(0,0,0,0.3);"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10]
});

// Component to fly to location
function FlyToLocation({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 14, { duration: 1.5 });
    }
  }, [map, position]);
  return null;
}

// Calculate distance between two points
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

export default function RestaurantMap({
  restaurants = [],
  userLocation = null,
  selectedRestaurant = null,
  onSelectRestaurant,
  radiusKm = 5,
  onGetDirections,
  showRadius = true
}) {
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);
  
  // Default center (Mexico City)
  const defaultCenter = userLocation 
    ? [userLocation.lat, userLocation.lng] 
    : [19.4326, -99.1332];

  // Filter restaurants within radius
  const nearbyRestaurants = restaurants.filter(r => {
    if (!r.coordinates || !userLocation) return true; // Show all if no location
    const distance = calculateDistance(
      userLocation.lat, 
      userLocation.lng, 
      r.coordinates.lat, 
      r.coordinates.lng
    );
    return distance <= radiusKm;
  });

  return (
    <div className="relative w-full h-full rounded-2xl overflow-hidden">
      <MapContainer
        center={defaultCenter}
        zoom={13}
        ref={mapRef}
        className="w-full h-full"
        whenReady={() => setMapReady(true)}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* Fly to selected restaurant */}
        {selectedRestaurant?.coordinates && (
          <FlyToLocation 
            position={[selectedRestaurant.coordinates.lat, selectedRestaurant.coordinates.lng]} 
          />
        )}
        
        {/* 5km radius circle */}
        {showRadius && userLocation && (
          <Circle
            center={[userLocation.lat, userLocation.lng]}
            radius={radiusKm * 1000}
            pathOptions={{
              color: '#F97316',
              fillColor: '#F97316',
              fillOpacity: 0.1,
              weight: 2,
              dashArray: '5, 10'
            }}
          />
        )}
        
        {/* User location marker */}
        {userLocation && (
          <Marker 
            position={[userLocation.lat, userLocation.lng]}
            icon={userLocationIcon}
          >
            <Popup>
              <div className="text-center p-1">
                <p className="font-semibold text-sm">Tu ubicación</p>
              </div>
            </Popup>
          </Marker>
        )}
        
        {/* Restaurant markers */}
        {nearbyRestaurants.map((restaurant) => {
          if (!restaurant.coordinates) return null;
          
          const isSelected = selectedRestaurant?.id === restaurant.id;
          const distance = userLocation 
            ? calculateDistance(
                userLocation.lat, 
                userLocation.lng, 
                restaurant.coordinates.lat, 
                restaurant.coordinates.lng
              ).toFixed(1)
            : null;
          
          return (
            <Marker
              key={restaurant.id}
              position={[restaurant.coordinates.lat, restaurant.coordinates.lng]}
              icon={createRestaurantIcon(isSelected ? '#F97316' : '#666666')}
              eventHandlers={{
                click: () => onSelectRestaurant?.(restaurant)
              }}
            >
              <Popup>
                <div className="min-w-[200px]">
                  <div className="flex items-center gap-2 mb-2">
                    {restaurant.logo_url ? (
                      <img 
                        src={restaurant.logo_url} 
                        alt={restaurant.name}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                        <Utensils className="w-5 h-5 text-orange-500" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-sm">{restaurant.name}</h3>
                      {distance && (
                        <p className="text-xs text-gray-500">{distance} km</p>
                      )}
                    </div>
                  </div>
                  
                  {restaurant.address && (
                    <p className="text-xs text-gray-600 mb-2 flex items-start gap-1">
                      <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      {restaurant.address}
                    </p>
                  )}
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => onSelectRestaurant?.(restaurant)}
                      className="flex-1 px-3 py-1.5 bg-orange-500 text-white text-xs font-medium rounded-lg hover:bg-orange-600 transition-colors"
                    >
                      Reservar
                    </button>
                    <button
                      onClick={() => onGetDirections?.(restaurant)}
                      className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Navigation className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
      
      {/* Radius indicator */}
      {showRadius && (
        <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg text-xs font-medium text-gray-700">
          <span className="text-orange-500">{nearbyRestaurants.length}</span> restaurantes a menos de <span className="text-orange-500">{radiusKm}km</span>
        </div>
      )}
      
      {/* Center on user button */}
      {userLocation && (
        <button
          onClick={() => {
            if (mapRef.current) {
              mapRef.current.flyTo([userLocation.lat, userLocation.lng], 14);
            }
          }}
          className="absolute bottom-3 right-3 p-2.5 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
        >
          <Navigation className="w-4 h-4 text-blue-500" />
        </button>
      )}
    </div>
  );
}
